// First-party landing analytics — anonymous session id + fire-and-forget
// events into POST /api/metrics (allow-listed server-side). Analytics must
// never break the page: every path is wrapped, failures are swallowed.
import { useEffect, useRef } from "react";

const SID_KEY = "icc.sid";

function sessionId(): string {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid) {
      sid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return "s-anonymous-fallback";
  }
}

const firedOnce = new Set<string>();

export function track(event: string, props?: Record<string, unknown>): void {
  try {
    const body = JSON.stringify({ event, session: sessionId(), props });
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/metrics", new Blob([body], { type: "application/json" }));
    } else {
      void fetch("/api/metrics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => undefined);
    }
  } catch {
    /* never break the page for analytics */
  }
}

/** Like track(), but at most once per page load (section-reached, funnels). */
export function trackOnce(event: string, props?: Record<string, unknown>): void {
  if (firedOnce.has(event)) return;
  firedOnce.add(event);
  track(event, props);
}

/** Fires `event` once when the referenced section scrolls into view. */
export function useSectionReached<T extends HTMLElement>(event: string) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          trackOnce(event);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [event]);
  return ref;
}
